/**
 * Production `AuthService` backed by the Firebase Web SDK. Wraps Firebase's
 * auth operations into the small `AuthService` surface the React layer uses.
 *
 * Google sign-in uses a popup (signInWithPopup). Email/password uses the
 * standard create/sign-in helpers.
 */

import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken as fbSignInWithCustomToken,
  signOut as fbSignOut,
  type Auth,
  type User,
} from "firebase/auth";
import type { AuthService, AuthStateListener, AuthUser } from "./authService.js";

function toAuthUser(user: User): AuthUser {
  return { uid: user.uid, email: user.email, displayName: user.displayName };
}

export function createFirebaseAuthService(auth: Auth): AuthService {
  const googleProvider = new GoogleAuthProvider();
  // Always show Google's account chooser so a user can pick a DIFFERENT account
  // (without this, Google silently reuses the single signed-in account).
  googleProvider.setCustomParameters({ prompt: "select_account" });

  return {
    onAuthStateChanged(listener: AuthStateListener): () => void {
      return onAuthStateChanged(auth, (user) => {
        listener(user ? toAuthUser(user) : null);
      });
    },
    async signInEmail(email, password) {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return toAuthUser(cred.user);
    },
    async signUpEmail(email, password) {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      return toAuthUser(cred.user);
    },
    async signInGoogle() {
      const cred = await signInWithPopup(auth, googleProvider);
      return toAuthUser(cred.user);
    },
    async signInWithCustomToken(token: string) {
      await fbSignInWithCustomToken(auth, token);
    },
    async signOut() {
      await fbSignOut(auth);
    },
    async getIdToken(forceRefresh = false) {
      const user = auth.currentUser;
      return user ? user.getIdToken(forceRefresh) : null;
    },
    async sendPasswordReset(email) {
      await sendPasswordResetEmail(auth, email);
    },
  };
}
